import * as React from "react";
import { FieldProps } from "formik";
import { Form } from "antd";
import { useState, useRef } from "react";

const FormItem = Form.Item;

export const InputOTPField: React.FC<
  FieldProps<any> & {
    description?: string;
    prefix: React.ReactNode;
    label?: string;
    useNumberComponent?: boolean;
    className?: string;
    pinLength?: number;
    otpLength?: number;
    disabled?: boolean;
    autoFocus?: boolean;
  }
> = ({
  field: { onChange, ...field },
  form: { touched, errors, setFieldValue, setFieldTouched },
  label,
  useNumberComponent = false,
  className = "",
  pinLength = 4,
  otpLength = 6,
  disabled = false,
  autoFocus = true,
  description,
  ...props
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(otpLength).fill(""));
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>(new Array(otpLength).fill(null));

  // Centralized error message
  const errorMsg =
    (touched[field.name] && errors[field.name]) ||
    (touched["otp"] && errors["otp"]) ||
    (touched["pin"] && errors["pin"]);

  // Initialize OTP from field value
  React.useEffect(() => {
    if (field.name === "otpPin" && field.value) {
      const otpArray = field.value.split("").slice(0, otpLength);
      const paddedOtp = [...otpArray, ...new Array(otpLength - otpArray.length).fill("")];
      setOtp(paddedOtp);
    }
  }, [field.value, field.name, otpLength]);

  // Auto-focus for OTP
  React.useEffect(() => {
    if (field.name === "otpPin" && autoFocus && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, [autoFocus, field.name]);

  // OTP input handlers
  const handleOtpChange = (index: number, val: string) => {
    if (disabled) return;
    const digit = val.replace(/[^0-9]/g, "");
    if (digit.length > 1) {
      handleOtpPaste(digit, index);
      return;
    }
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    setFieldValue(field.name, newOtp.join(""));
    if (digit && index < otpLength - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        otpInputRefs.current[index - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
        setFieldValue(field.name, newOtp.join(""));
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < otpLength - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpFocus = (index: number) => {
    otpInputRefs.current[index]?.select();
  };

  const handleOtpBlur = () => {
    setFieldTouched(field.name, true);
  };

  const handleOtpPaste = (pastedData: string, startIndex: number) => {
    const digits = pastedData.replace(/[^0-9]/g, "").split("");
    const newOtp = [...otp];
    digits.forEach((digit, i) => {
      const targetIndex = startIndex + i;
      if (targetIndex < otpLength) {
        newOtp[targetIndex] = digit;
      }
    });
    setOtp(newOtp);
    setFieldValue(field.name, newOtp.join(""));
    const nextEmptyIndex = newOtp.findIndex((val) => val === "");
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : otpLength - 1;
    otpInputRefs.current[focusIndex]?.focus();
  };

  const getOtpInputClasses = () => {
    let classes =
      "w-[54px] h-[55px] text-center rounded-[10px] !text-white !bg-[#1E1E1E] text-lg font-semibold transition-all duration-200 focus:outline-none focus:ring-2";
    if (disabled) {
      classes += " bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:border-gray-700";
    } else if (errorMsg) {
      classes += " border-red-500 text-red-600 focus:ring-red-500/20 bg-red-50 dark:bg-red-950/20 dark:border-red-500 dark:text-red-400";
    } else {
      classes += " text-gray-800 focus:border-[#525571] focus:ring-[#525571]/20 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:focus:border-[#525571]";
    }
    return classes;
  };

  return (
    <FormItem
      label={label}
      help={
        errorMsg ? (
          <div style={{ textAlign: "center" }} className="text-red-500 text-sm mt-1">
              {typeof errorMsg === "string"
                ? errorMsg
                : Array.isArray(errorMsg)
                ? errorMsg.join(", ")
                : null}
          </div>
        ) : null
      }
      validateStatus={errorMsg ? "error" : undefined}
    >
      <div className="w-full">
        {label && <div className="text-white text-sm mb-2">{label}</div>}
        <div className={`flex justify-center gap-3 ${className}`}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(ref) => {
                if (otpInputRefs.current) otpInputRefs.current[index] = ref;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(index, e)}
              onFocus={() => handleOtpFocus(index)}
              onBlur={handleOtpBlur}
              onPaste={(e) => {
                e.preventDefault();
                const pastedData = e.clipboardData.getData("text");
                handleOtpPaste(pastedData, index);
              }}
              className={getOtpInputClasses()}
              disabled={disabled}
              maxLength={1}
            />
          ))}
        </div>
      </div>
    </FormItem>
  );
};